import { useState, useEffect } from 'react'
import { motion } from 'framer-motion'
import { useOrganizationId } from '../hooks/useOrganizationId'
import { useOrganization } from '../contexts/OrganizationContext'
import Card from '../components/Card'
import Button from '../components/Button'
import Input from '../components/Input'
import Select from '../components/Select'
import Textarea from '../components/Textarea'
import {
  Shield,
  Download,
  Trash2,
  FileText,
  CheckCircle2,
  Clock,
  XCircle,
  Plus,
  Eye,
} from 'lucide-react'
import {
  createDataSubjectRequest,
  getDataSubjectRequests,
  processDataSubjectRequest,
  exportClientData,
  deleteClientData,
  recordConsent,
  getConsentRecords,
} from '../services/gdpr'
import { formatDate } from '../utils/helpers'

const GDPRManagement = () => {
  const [requests, setRequests] = useState([])
  const [consents, setConsents] = useState([])
  const [loading, setLoading] = useState(true)
  const [showRequestForm, setShowRequestForm] = useState(false)
  const [showConsentForm, setShowConsentForm] = useState(false)
  const [selectedClient, setSelectedClient] = useState(null)
  const [requestForm, setRequestForm] = useState({
    clientId: '',
    requestType: 'access',
    details: '',
  })
  const [consentForm, setConsentForm] = useState({
    clientId: '',
    consentType: 'data_processing',
    consentStatus: 'granted',
    consentText: '',
  })
  const [submitting, setSubmitting] = useState(false)
  const organizationId = useOrganizationId()
  const { isAdmin, canWrite } = useOrganization()

  useEffect(() => {
    if (organizationId) {
      fetchData()
    }
  }, [organizationId])

  const fetchData = async () => {
    setLoading(true)
    try {
      const [requestsData, consentsData] = await Promise.all([
        getDataSubjectRequests(organizationId),
        organizationId ? getConsentRecords(organizationId, null) : Promise.resolve([]),
      ])
      setRequests(requestsData)
      setConsents(consentsData)
    } catch (error) {
      console.error('Error fetching GDPR data:', error)
    } finally {
      setLoading(false)
    }
  }

  const handleCreateRequest = async (e) => {
    e.preventDefault()
    if (!requestForm.clientId || !organizationId) return

    try {
      setSubmitting(true)
      await createDataSubjectRequest(
        organizationId,
        requestForm.clientId,
        requestForm.requestType,
        { details: requestForm.details }
      )
      setRequestForm({ clientId: '', requestType: 'access', details: '' })
      setShowRequestForm(false)
      fetchData()
      alert('Data subject request created successfully')
    } catch (error) {
      alert(error.message || 'Failed to create request')
    } finally {
      setSubmitting(false)
    }
  }

  const handleProcessRequest = async (requestId, status) => {
    try {
      let responseData = null
      if (status === 'completed' && requestForm.requestType === 'access') {
        // Export data for access request
        const data = await exportClientData(organizationId, requestForm.clientId)
        responseData = data
      }

      await processDataSubjectRequest(requestId, organizationId, status, responseData)
      fetchData()
      alert(`Request ${status}`)
    } catch (error) {
      alert(error.message || 'Failed to process request')
    }
  }

  const handleExportData = async (clientId) => {
    try {
      const data = await exportClientData(organizationId, clientId)
      const blob = new Blob([JSON.stringify(data, null, 2)], { type: 'application/json' })
      const url = URL.createObjectURL(blob)
      const a = document.createElement('a')
      a.href = url
      a.download = `client-data-${clientId}-${Date.now()}.json`
      document.body.appendChild(a)
      a.click()
      document.body.removeChild(a)
      URL.revokeObjectURL(url)
      alert('Data exported successfully')
    } catch (error) {
      alert(error.message || 'Failed to export data')
    }
  }

  const handleDeleteData = async (clientId) => {
    if (
      !confirm(
        'Are you sure you want to delete all data for this client? This action cannot be undone and will delete all records, examinations, care plans, therapies, and sessions.'
      )
    )
      return

    try {
      await deleteClientData(organizationId, clientId, 'GDPR Right to Erasure request')
      alert('Client data deleted successfully')
      fetchData()
    } catch (error) {
      alert(error.message || 'Failed to delete data')
    }
  }

  const handleRecordConsent = async (e) => {
    e.preventDefault()
    if (!consentForm.clientId || !organizationId) return

    try {
      setSubmitting(true)
      await recordConsent(
        organizationId,
        consentForm.clientId,
        consentForm.consentType,
        consentForm.consentStatus,
        consentForm.consentText
      )
      setConsentForm({ clientId: '', consentType: 'data_processing', consentStatus: 'granted', consentText: '' })
      setShowConsentForm(false)
      fetchData()
      alert('Consent recorded successfully')
    } catch (error) {
      alert(error.message || 'Failed to record consent')
    } finally {
      setSubmitting(false)
    }
  }

  const getStatusColor = (status) => {
    switch (status) {
      case 'completed':
        return 'bg-green-100 text-green-700'
      case 'in_progress':
        return 'bg-yellow-100 text-yellow-700'
      case 'rejected':
        return 'bg-red-100 text-red-700'
      default:
        return 'bg-gray-100 text-gray-700'
    }
  }

  if (loading) {
    return (
      <div className="section-container">
        <p className="text-gray-500">Loading GDPR management...</p>
      </div>
    )
  }

  return (
    <div className="section-container space-y-6">
      <div className="flex items-center gap-3 mb-6">
        <div className="w-12 h-12 rounded-2xl bg-gradient-to-br from-green-500 to-green-600 flex items-center justify-center shadow-lg">
          <Shield size={24} className="text-white" />
        </div>
        <div>
          <h1 className="text-3xl font-bold text-gray-900 dark:text-white">GDPR Compliance</h1>
          <p className="text-gray-600 dark:text-gray-400">Manage data subject requests and consent records</p>
        </div>
      </div>

      {/* Data Subject Requests */}
      <Card variant="soft" className="p-6">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-xl font-semibold text-gray-900 dark:text-white">Data Subject Requests</h3>
          {canWrite('clients') && (
            <Button variant="secondary" size="sm" onClick={() => setShowRequestForm(true)}>
              <Plus size={16} className="mr-1" />
              New Request
            </Button>
          )}
        </div>

        {showRequestForm && (
          <Card variant="soft" className="p-4 mb-4 bg-blue-50 border border-blue-200">
            <h4 className="font-semibold text-gray-900 mb-3">Create Data Subject Request</h4>
            <form onSubmit={handleCreateRequest} className="space-y-3">
              <Input
                label="Client ID"
                value={requestForm.clientId}
                onChange={(e) => setRequestForm({ ...requestForm, clientId: e.target.value })}
                placeholder="Enter client UUID"
                required
              />
              <Select
                label="Request Type"
                options={[
                  { value: 'access', label: 'Right to Access' },
                  { value: 'portability', label: 'Data Portability' },
                  { value: 'erasure', label: 'Right to Erasure' },
                  { value: 'rectification', label: 'Rectification' },
                  { value: 'restriction', label: 'Restriction of Processing' },
                ]}
                value={requestForm.requestType}
                onChange={(e) => setRequestForm({ ...requestForm, requestType: e.target.value })}
                required
              />
              <Textarea
                label="Details"
                value={requestForm.details}
                onChange={(e) => setRequestForm({ ...requestForm, details: e.target.value })}
                placeholder="Additional information about the request..."
                rows={3}
              />
              <div className="flex gap-3">
                <Button type="submit" disabled={submitting} className="flex-1">
                  {submitting ? 'Creating...' : 'Create Request'}
                </Button>
                <Button
                  type="button"
                  variant="secondary"
                  onClick={() => setShowRequestForm(false)}
                  className="flex-1"
                >
                  Cancel
                </Button>
              </div>
            </form>
          </Card>
        )}

        {requests.length === 0 ? (
          <p className="text-gray-500 text-sm text-center py-8">No data subject requests</p>
        ) : (
          <div className="space-y-3">
            {requests.map((request) => (
              <Card key={request.id} variant="soft" className="p-4">
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <div className="flex items-center gap-2 mb-2">
                      <span className="font-semibold text-gray-900 capitalize">
                        {request.request_type}
                      </span>
                      <span
                        className={`px-2 py-1 rounded-full text-xs font-semibold ${getStatusColor(
                          request.status
                        )}`}
                      >
                        {request.status}
                      </span>
                    </div>
                    {request.client && (
                      <p className="text-sm text-gray-600">
                        Client: {request.client.first_name} {request.client.last_name}
                      </p>
                    )}
                    {request.request_details && (
                      <p className="text-xs text-gray-500 mt-1">
                        {JSON.stringify(request.request_details)}
                      </p>
                    )}
                    <p className="text-xs text-gray-500 mt-2">
                      Requested: {formatDate(request.created_at)}
                    </p>
                  </div>
                  {isAdmin && request.status === 'pending' && (
                    <div className="flex gap-2">
                      <Button
                        variant="secondary"
                        size="sm"
                        onClick={() => handleProcessRequest(request.id, 'in_progress')}
                      >
                        Start Processing
                      </Button>
                      {request.request_type === 'access' && (
                        <Button
                          variant="secondary"
                          size="sm"
                          onClick={() => handleExportData(request.client_id)}
                        >
                          <Download size={14} className="mr-1" />
                          Export
                        </Button>
                      )}
                      {request.request_type === 'erasure' && (
                        <Button
                          variant="secondary"
                          size="sm"
                          onClick={() => handleDeleteData(request.client_id)}
                          className="text-red-600"
                        >
                          <Trash2 size={14} className="mr-1" />
                          Delete
                        </Button>
                      )}
                    </div>
                  )}
                </div>
              </Card>
            ))}
          </div>
        )}
      </Card>

      {/* Consent Records */}
      <Card variant="soft" className="p-6">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-xl font-semibold text-gray-900">Consent Records</h3>
          {canWrite('clients') && (
            <Button variant="secondary" size="sm" onClick={() => setShowConsentForm(true)}>
              <Plus size={16} className="mr-1" />
              Record Consent
            </Button>
          )}
        </div>

        {showConsentForm && (
          <Card variant="soft" className="p-4 mb-4 bg-blue-50 border border-blue-200">
            <h4 className="font-semibold text-gray-900 mb-3">Record Consent</h4>
            <form onSubmit={handleRecordConsent} className="space-y-3">
              <Input
                label="Client ID"
                value={consentForm.clientId}
                onChange={(e) => setConsentForm({ ...consentForm, clientId: e.target.value })}
                placeholder="Enter client UUID"
                required
              />
              <Select
                label="Consent Type"
                options={[
                  { value: 'data_processing', label: 'Data Processing' },
                  { value: 'marketing', label: 'Marketing' },
                  { value: 'research', label: 'Research' },
                  { value: 'sharing', label: 'Data Sharing' },
                ]}
                value={consentForm.consentType}
                onChange={(e) => setConsentForm({ ...consentForm, consentType: e.target.value })}
                required
              />
              <Select
                label="Consent Status"
                options={[
                  { value: 'granted', label: 'Granted' },
                  { value: 'denied', label: 'Denied' },
                  { value: 'withdrawn', label: 'Withdrawn' },
                ]}
                value={consentForm.consentStatus}
                onChange={(e) => setConsentForm({ ...consentForm, consentStatus: e.target.value })}
                required
              />
              <Textarea
                label="Consent Text"
                value={consentForm.consentText}
                onChange={(e) => setConsentForm({ ...consentForm, consentText: e.target.value })}
                placeholder="The text shown to the client when consent was requested..."
                rows={3}
              />
              <div className="flex gap-3">
                <Button type="submit" disabled={submitting} className="flex-1">
                  {submitting ? 'Recording...' : 'Record Consent'}
                </Button>
                <Button
                  type="button"
                  variant="secondary"
                  onClick={() => setShowConsentForm(false)}
                  className="flex-1"
                >
                  Cancel
                </Button>
              </div>
            </form>
          </Card>
        )}

        {consents.length === 0 ? (
          <p className="text-gray-500 text-sm text-center py-8">No consent records</p>
        ) : (
          <div className="space-y-3">
            {consents.map((consent) => (
              <Card key={consent.id} variant="soft" className="p-4">
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <div className="flex items-center gap-2 mb-2">
                      <span className="font-semibold text-gray-900 capitalize">
                        {consent.consent_type.replace('_', ' ')}
                      </span>
                      <span
                        className={`px-2 py-1 rounded-full text-xs font-semibold ${
                          consent.consent_status === 'granted'
                            ? 'bg-green-100 text-green-700'
                            : consent.consent_status === 'withdrawn'
                            ? 'bg-red-100 text-red-700'
                            : 'bg-gray-100 text-gray-700'
                        }`}
                      >
                        {consent.consent_status}
                      </span>
                    </div>
                    {consent.consent_text && (
                      <p className="text-sm text-gray-600 mb-2">{consent.consent_text}</p>
                    )}
                    <p className="text-xs text-gray-500">
                      {consent.granted_at && `Granted: ${formatDate(consent.granted_at)}`}
                      {consent.withdrawn_at && ` • Withdrawn: ${formatDate(consent.withdrawn_at)}`}
                      {!consent.granted_at && !consent.withdrawn_at &&
                        `Recorded: ${formatDate(consent.created_at)}`}
                    </p>
                  </div>
                </div>
              </Card>
            ))}
          </div>
        )}
      </Card>
    </div>
  )
}

export default GDPRManagement

